import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useAuth } from '../../../context/AuthContext';
import { adminService } from '../../../services/adminService';
import { extractList } from '../../../services/api';
import { parsePeriod, ROLES } from '../../../constants';
import Loading from '../../../shared/Loading';
import EmptyState from '../../../shared/EmptyState';
import Badge from '../../../shared/Badge';

const CARDS = [
  { key: 'total_reports', label: 'Total Laporan', color: '#12b7ad' },
  { key: 'draft', label: 'Draft', color: '#f59e0b' },
  { key: 'submitted', label: 'Submitted', color: '#3b82f6' },
  { key: 'approved', label: 'Approved', color: '#10b981' },
  { key: 'rejected', label: 'Rejected', color: '#ef4444' },
];

const PIE_COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444'];

const QUICK_ACTIONS = [
  { label: 'Uniform Reports', icon: '\uD83D\uDCC4', path: '/admin/uniform-reports' },
  { label: 'Tambah Seragam', icon: '\uD83D\uDC55', path: '/admin/uniforms' },
  { label: 'Tambah Kategori', icon: '\uD83D\uDDC2\uFE0F', path: '/admin/uniform-categories' },
  { label: 'Kelola Users', icon: '\uD83D\uDC64', path: '/admin/users' },
];

const NUMBER_FORMATTER = new Intl.NumberFormat('id-ID');

const monthInputToPeriod = (value) => (value ? value.replace('-', '') : undefined);

const getCurrentMonthInput = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const normalizeCompanyId = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const getPeriodLabel = (period) => {
  if (!period) return '-';
  const { month, year, label } = parsePeriod(period);
  if (!Number.isFinite(month) || !Number.isFinite(year) || !label) return String(period);
  return label;
};

const getDisplayWilayah = (value) => {
  if (typeof value !== 'string') return '-';
  const trimmed = value.trim();
  return trimmed || '-';
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatNumber = (value) => NUMBER_FORMATTER.format(toNumber(value));

const formatDelta = (value) => {
  const numeric = toNumber(value);
  if (numeric > 0) return `+${NUMBER_FORMATTER.format(numeric)}`;
  return NUMBER_FORMATTER.format(numeric);
};

const getUniformOptionLabel = (uniform) => {
  const code = String(uniform?.code || '').trim();
  const name = String(uniform?.name || '').trim();
  if (code && name) return `${code} - ${name}`;
  return code || name || `Uniform ${uniform?.uniform_id ?? '-'}`;
};

const UniformMultiSelect = ({
  id,
  options,
  selectedValues,
  onChange,
  placeholder = 'Pilih uniform...',
}) => {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const normalizedOptions = useMemo(
    () => (Array.isArray(options) ? options : []).map((option) => ({
      value: String(option.uniform_id),
      label: getUniformOptionLabel(option),
    })),
    [options]
  );

  const selectedSet = useMemo(
    () => new Set((Array.isArray(selectedValues) ? selectedValues : []).map((value) => String(value))),
    [selectedValues]
  );

  const selectedOptions = useMemo(
    () => normalizedOptions.filter((option) => selectedSet.has(option.value)),
    [normalizedOptions, selectedSet]
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return normalizedOptions;
    return normalizedOptions.filter((option) => option.label.toLowerCase().includes(normalizedQuery));
  }, [normalizedOptions, query]);

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (event) => {
      if (!rootRef.current || rootRef.current.contains(event.target)) return;
      setOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const summaryLabel = useMemo(() => {
    if (!selectedOptions.length) return placeholder;
    if (selectedOptions.length === 1) return selectedOptions[0].label;
    if (selectedOptions.length === 2) return `${selectedOptions[0].label}, ${selectedOptions[1].label}`;
    return `${selectedOptions[0].label}, ${selectedOptions[1].label} +${selectedOptions.length - 2} lainnya`;
  }, [placeholder, selectedOptions]);

  const toggleValue = (value) => {
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(Array.from(next));
  };

  const handleSelectAll = () => {
    onChange(normalizedOptions.map((option) => option.value));
  };

  const handleClear = () => {
    onChange([]);
  };

  return (
    <div ref={rootRef} className={`dash-multiselect${open ? ' is-open' : ''}`}>
      <button
        id={id}
        type="button"
        className="dash-multiselect__trigger"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="dash-multiselect__summary">{summaryLabel}</span>
        <span className="dash-multiselect__meta">{selectedOptions.length} dipilih</span>
      </button>

      {open && (
        <div className="dash-multiselect__panel">
          <div className="dash-multiselect__search-wrap">
            <input
              type="text"
              className="dash-multiselect__search"
              placeholder="Cari uniform..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          <div className="dash-multiselect__actions">
            <button type="button" onClick={handleSelectAll} disabled={!normalizedOptions.length}>
              Pilih Semua
            </button>
            <button type="button" onClick={handleClear} disabled={!selectedOptions.length}>
              Kosongkan
            </button>
          </div>

          <div className="dash-multiselect__options" role="listbox" aria-multiselectable="true">
            {!filteredOptions.length ? (
              <div className="dash-multiselect__empty">Uniform tidak ditemukan</div>
            ) : (
              filteredOptions.map((option) => (
                <label key={option.value} className="dash-multiselect__option">
                  <input
                    type="checkbox"
                    checked={selectedSet.has(option.value)}
                    onChange={() => toggleValue(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperadmin = user?.role === ROLES.SUPERADMIN;

  const [summary, setSummary] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [totalsMap, setTotalsMap] = useState({});
  const [uniforms, setUniforms] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [periodFilter, setPeriodFilter] = useState(getCurrentMonthInput());
  const [wilayahFilter, setWilayahFilter] = useState('');
  const [uniformIdsFilter, setUniformIdsFilter] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');

  const [uniformSummary, setUniformSummary] = useState(null);
  const [uniformPivot, setUniformPivot] = useState(null);
  const [uniformSummaryLoading, setUniformSummaryLoading] = useState(true);
  const [uniformPivotLoading, setUniformPivotLoading] = useState(true);
  const [uniformSummaryError, setUniformSummaryError] = useState('');
  const [uniformPivotError, setUniformPivotError] = useState('');

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminService.getUsers({ limit: 1000 }).then((res) => {
      const map = {};
      extractList(res.data).forEach((target) => { map[target.user_id] = target; });
      setUsersMap(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    adminService.getUniforms({ page: 1, limit: 1000 }).then((res) => {
      setUniforms(extractList(res.data));
    }).catch(() => {
      setUniforms([]);
    });
  }, []);

  useEffect(() => {
    if (!isSuperadmin) {
      setCompanies([]);
      setCompanyFilter('');
      return;
    }

    adminService.getCompanies({ page: 1, limit: 1000 }).then((res) => {
      setCompanies(extractList(res.data));
    }).catch(() => {
      setCompanies([]);
    });
  }, [isSuperadmin]);

  useEffect(() => {
    Promise.all([
      adminService.getDashboardSummary(),
      adminService.getReports({ page: 1, limit: 5 }),
    ]).then(([summaryRes, reportRes]) => {
      setSummary(summaryRes.data);
      const list = extractList(reportRes.data);
      setRecentReports(list);

      Promise.all(
        list.map((report) => (
          adminService.getReportDetails(report.uniform_report_id, { page: 1, limit: 100 })
            .then((detailRes) => {
              const items = extractList(detailRes.data);
              const total = items.reduce((sum, detail) => sum + toNumber(detail.qty), 0);
              return { id: report.uniform_report_id, total };
            })
            .catch(() => ({ id: report.uniform_report_id, total: toNumber(report.total_uniform_received) }))
        ))
      ).then((results) => {
        const map = {};
        results.forEach(({ id, total }) => { map[id] = total; });
        setTotalsMap(map);
      });
    }).catch(() => {
      setSummary(null);
      setRecentReports([]);
      setTotalsMap({});
    }).finally(() => setLoading(false));
  }, []);

  const buildDashboardFilterParams = useCallback(({
    periodValue = periodFilter,
    wilayahValue = wilayahFilter,
    uniformIdsValue = uniformIdsFilter,
    companyValue = companyFilter,
  } = {}) => {
    const normalizedUniformIds = (Array.isArray(uniformIdsValue) ? uniformIdsValue : [])
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item));

    return {
      period: monthInputToPeriod(periodValue),
      wilayah: wilayahValue || undefined,
      uniform_ids: normalizedUniformIds.length ? normalizedUniformIds : undefined,
      company_id: isSuperadmin ? normalizeCompanyId(companyValue) : undefined,
    };
  }, [companyFilter, isSuperadmin, periodFilter, uniformIdsFilter, wilayahFilter]);

  const fetchUniformDashboardData = useCallback(async (overrides = {}) => {
    const params = buildDashboardFilterParams(overrides);
    setUniformSummaryLoading(true);
    setUniformPivotLoading(true);
    setUniformSummaryError('');
    setUniformPivotError('');

    const [summaryResult, pivotResult] = await Promise.allSettled([
      adminService.getDashboardUniformSummary(params),
      adminService.getDashboardUniformPivot(params),
    ]);

    if (summaryResult.status === 'fulfilled') {
      setUniformSummary(summaryResult.value?.data || null);
    } else {
      setUniformSummary(null);
      setUniformSummaryError(summaryResult.reason?.message || 'Gagal memuat uniform summary');
    }
    setUniformSummaryLoading(false);

    if (pivotResult.status === 'fulfilled') {
      setUniformPivot(pivotResult.value?.data || null);
    } else {
      setUniformPivot(null);
      setUniformPivotError(pivotResult.reason?.message || 'Gagal memuat uniform pivot');
    }
    setUniformPivotLoading(false);
  }, [buildDashboardFilterParams]);

  useEffect(() => {
    fetchUniformDashboardData();
  }, [fetchUniformDashboardData]);

  if (loading) return <Loading />;
  if (!summary) {
    return <EmptyState icon="\u26A0\uFE0F" title="Gagal memuat dashboard" desc="Periksa koneksi atau coba lagi." />;
  }

  const pieData = [
    { name: 'Draft', value: summary.draft || 0 },
    { name: 'Submitted', value: summary.submitted || 0 },
    { name: 'Approved', value: summary.approved || 0 },
    { name: 'Rejected', value: summary.rejected || 0 },
  ].filter((item) => item.value > 0);

  const barRaw = {};
  recentReports.forEach((report) => {
    const label = parsePeriod(report.period).label;
    barRaw[label] = (barRaw[label] || 0) + (totalsMap[report.uniform_report_id] ?? toNumber(report.total_uniform_received));
  });
  const barData = Object.entries(barRaw).map(([period, total]) => ({ period, total }));

  const summaryRows = Array.isArray(uniformSummary?.data) ? uniformSummary.data : [];
  const summaryChartData = summaryRows.map((item) => ({
    code: item.uniform_code || `#${item.uniform_id}`,
    name: item.uniform_name || item.uniform_code || `Uniform ${item.uniform_id}`,
    total_qty: toNumber(item.total_qty),
  }));
  const summaryPeriodLabel = getPeriodLabel(uniformSummary?.period || monthInputToPeriod(periodFilter));
  const summaryGrandTotalQty = uniformSummary?.grand_total_qty
    ?? summaryRows.reduce((sum, item) => sum + toNumber(item.total_qty), 0);
  const summaryTotalUniforms = uniformSummary?.total_uniforms ?? summaryRows.length;

  const pivotColumns = Array.isArray(uniformPivot?.columns) ? uniformPivot.columns : [];
  const pivotRows = Array.isArray(uniformPivot?.rows) ? uniformPivot.rows : [];
  const pivotPeriodLabel = getPeriodLabel(uniformPivot?.period || monthInputToPeriod(periodFilter));
  const pivotPreviousPeriodLabel = getPeriodLabel(uniformPivot?.previous_period);
  const pivotEmptyColSpan = pivotColumns.length + 4;

  const wilayahOptions = [...new Set(
    Object.values(usersMap)
      .map((target) => (typeof target?.wilayah === 'string' ? target.wilayah.trim() : ''))
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b, 'id', { sensitivity: 'base' }));

  const handleResetFilters = () => {
    setPeriodFilter(getCurrentMonthInput());
    setWilayahFilter('');
    setUniformIdsFilter([]);
    setCompanyFilter('');
  };

  return (
    <div className="dash-root">
      <div className="admin-summary-grid">
        {CARDS.map(({ key, label, color }) => (
          <div key={key} className="admin-summary-card" style={{ borderLeftColor: color }}>
            <div className="admin-summary-card__value">{summary[key]}</div>
            <div className="admin-summary-card__label">{label}</div>
          </div>
        ))}
      </div>

      <div className="dash-mid-grid">
        <div className="dash-card">
          <div className="dash-card__title">Pcs per Periode</div>
          {barData.length === 0 ? (
            <div className="dash-empty-chart">Belum ada data</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={barData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip formatter={(value) => [`${value} pcs`, 'Total']} />
                <Bar dataKey="total" fill="#12b7ad" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="dash-card">
          <div className="dash-card__title">Distribusi Status</div>
          {pieData.length === 0 ? (
            <div className="dash-empty-chart">Belum ada data</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                  fontSize={11}
                >
                  {pieData.map((_, index) => <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
                </Pie>
                <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="dash-card dash-quick-actions">
          <div className="dash-card__title">Quick Actions</div>
          <div className="dash-qa-grid">
            {QUICK_ACTIONS.map(({ label, icon, path }) => (
              <button key={path} className="dash-qa-btn" onClick={() => navigate(path)}>
                <span className="dash-qa-icon">{icon}</span>
                <span className="dash-qa-label">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="dash-card">
        <div className="dash-card__title">Filter Dashboard Seragam</div>
        <div className="dash-filter-grid">
          <div className="dash-filter-field">
            <label htmlFor="dashboard-period-filter">Periode</label>
            <input
              id="dashboard-period-filter"
              type="month"
              className="admin-filter-select"
              value={periodFilter}
              onChange={(event) => setPeriodFilter(event.target.value)}
            />
          </div>

          <div className="dash-filter-field dash-filter-field--uniform">
            <label htmlFor="dashboard-uniform-filter">Uniform (multi select)</label>
            <UniformMultiSelect
              id="dashboard-uniform-filter"
              options={uniforms}
              selectedValues={uniformIdsFilter}
              onChange={setUniformIdsFilter}
            />
          </div>

          <div className="dash-filter-field">
            <label htmlFor="dashboard-wilayah-filter">Wilayah</label>
            <select
              id="dashboard-wilayah-filter"
              className="admin-filter-select"
              value={wilayahFilter}
              onChange={(event) => setWilayahFilter(event.target.value)}
            >
              <option value="">Semua Wilayah</option>
              {wilayahOptions.map((wilayah) => (
                <option key={wilayah} value={wilayah}>{wilayah}</option>
              ))}
            </select>
          </div>

          {isSuperadmin && (
            <div className="dash-filter-field">
              <label htmlFor="dashboard-company-filter">Company</label>
              <select
                id="dashboard-company-filter"
                className="admin-filter-select"
                value={companyFilter}
                onChange={(event) => setCompanyFilter(event.target.value)}
              >
                <option value="">Semua Company</option>
                {companies.map((company) => (
                  <option key={company.company_id} value={company.company_id}>{company.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="dash-filter-field dash-filter-field--action">
            <button type="button" className="admin-btn-secondary" onClick={handleResetFilters}>
              Reset Filter
            </button>
            <button type="button" className="admin-btn-primary" onClick={() => fetchUniformDashboardData()}>
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="admin-section-header">
        <h3 className="admin-section-title">Uniform Summary</h3>
      </div>
      <div className="dash-summary-meta">
        <div className="dash-summary-meta__item">
          <span>Periode</span>
          <strong>{summaryPeriodLabel}</strong>
        </div>
        <div className="dash-summary-meta__item">
          <span>Total Uniforms</span>
          <strong>{formatNumber(summaryTotalUniforms)}</strong>
        </div>
        <div className="dash-summary-meta__item">
          <span>Grand Total Qty</span>
          <strong>{formatNumber(summaryGrandTotalQty)}</strong>
        </div>
      </div>

      {uniformSummaryLoading ? (
        <div className="dash-inline-state">Memuat uniform summary...</div>
      ) : uniformSummaryError ? (
        <div className="dash-inline-state dash-inline-state--error">{uniformSummaryError}</div>
      ) : (
        <div className="dash-uniform-summary-grid">
          <div className="dash-card">
            <div className="dash-card__title">Grafik Total Qty per Uniform</div>
            {summaryChartData.length === 0 ? (
              <div className="dash-empty-chart">Tidak ada data summary</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={summaryChartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <XAxis dataKey="code" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip
                    formatter={(value) => [`${formatNumber(value)} pcs`, 'Total']}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.name || label}
                  />
                  <Bar dataKey="total_qty" fill="#12b7ad" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Kode</th>
                  <th>Nama Seragam</th>
                  <th>Total Qty</th>
                </tr>
              </thead>
              <tbody>
                {summaryRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center', color: '#9ca3af', padding: '20px' }}>
                      Tidak ada data summary
                    </td>
                  </tr>
                ) : summaryRows.map((item) => (
                  <tr key={item.uniform_id}>
                    <td>{item.uniform_code || '-'}</td>
                    <td>{item.uniform_name || '-'}</td>
                    <td>{formatNumber(item.total_qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="admin-section-header">
        <h3 className="admin-section-title">Uniform Pivot per Wilayah</h3>
      </div>
      <p className="admin-page__meta">
        Periode: {pivotPeriodLabel} | Sebelumnya: {pivotPreviousPeriodLabel}
      </p>
      <p className="admin-page__meta">
        Format kolom uniform: <strong>awal / total / selisih</strong>
      </p>

      {uniformPivotLoading ? (
        <div className="dash-inline-state">Memuat uniform pivot...</div>
      ) : uniformPivotError ? (
        <div className="dash-inline-state dash-inline-state--error">{uniformPivotError}</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table dash-pivot-table">
            <thead>
              <tr>
                <th>Wilayah</th>
                {pivotColumns.map((column) => (
                  <th key={column.uniform_id}>
                    {column.uniform_code || column.uniform_name || `Uniform ${column.uniform_id}`}
                  </th>
                ))}
                <th>Total Awal</th>
                <th>Total</th>
                <th>Total Selisih</th>
              </tr>
            </thead>
            <tbody>
              {pivotRows.length === 0 ? (
                <tr>
                  <td colSpan={pivotEmptyColSpan} style={{ textAlign: 'center', color: '#9ca3af', padding: '20px' }}>
                    Tidak ada data pivot
                  </td>
                </tr>
              ) : pivotRows.map((row) => {
                const uniformMap = {};
                (Array.isArray(row.uniforms) ? row.uniforms : []).forEach((item) => {
                  uniformMap[item.uniform_id] = item;
                });

                return (
                  <tr key={`${row.wilayah || 'unknown'}-${row.period || ''}`}>
                    <td>{getDisplayWilayah(row.wilayah)}</td>
                    {pivotColumns.map((column) => {
                      const data = uniformMap[column.uniform_id] || {};
                      const diff = toNumber(data.selisih);
                      const className = [
                        'dash-pivot-cell__selisih',
                        diff > 0 ? 'dash-pivot-cell__selisih--pos' : '',
                        diff < 0 ? 'dash-pivot-cell__selisih--neg' : '',
                      ].filter(Boolean).join(' ');

                      return (
                        <td key={`${row.wilayah || 'unknown'}-${column.uniform_id}`}>
                          <div className="dash-pivot-cell">
                            <div className="dash-pivot-line">
                              <span className="dash-pivot-label">Awal:</span>
                              <span className="dash-pivot-cell__awal">{formatNumber(data.awal)}</span>
                            </div>
                            <div className="dash-pivot-line">
                              <span className="dash-pivot-label">Total:</span>
                              <span className="dash-pivot-cell__total">{formatNumber(data.total)}</span>
                            </div>
                            <div className="dash-pivot-line">
                              <span className="dash-pivot-label">Selisih:</span>
                              <span className={className}>{formatDelta(data.selisih)}</span>
                            </div>
                          </div>
                        </td>
                      );
                    })}
                    <td>{formatNumber(row.total_awal)}</td>
                    <td>{formatNumber(row.total)}</td>
                    <td>{formatDelta(row.total_selisih)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="admin-section-header">
        <h3 className="admin-section-title">Laporan Terbaru</h3>
        <button className="admin-btn-link" onClick={() => navigate('/admin/uniform-reports')}>
          Lihat semua
        </button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Periode</th>
              <th>Employee</th>
              <th>Total Pcs</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {recentReports.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', color: '#9ca3af', padding: '20px' }}>
                  Belum ada laporan
                </td>
              </tr>
            ) : recentReports.map((report) => (
              <tr
                key={report.uniform_report_id}
                className="admin-table__row--clickable"
                onClick={() => navigate(`/admin/uniform-reports/${report.uniform_report_id}`)}
              >
                <td>{parsePeriod(report.period).label}</td>
                <td>{report.user?.name || usersMap[report.user_id]?.name || '\u2014'}</td>
                <td>{formatNumber(totalsMap[report.uniform_report_id] ?? report.total_uniform_received)} pcs</td>
                <td><Badge status={report.report_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DashboardPage;
